import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  TWITTER_HANDLE,
  getJsonLdForPath,
  getSeoForPath,
} from "../seo";

function upsertMetaByName(name: string, content: string) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const JSON_LD_ID = "totties-json-ld";

/** Updates document head for SEO, social sharing, and structured data. */
const SeoHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const image = seo.image ?? DEFAULT_OG_IMAGE;

    document.title = seo.title;
    document.documentElement.lang = "en-ZA";

    upsertMetaByName("description", seo.description);
    upsertMetaByName("keywords", seo.keywords);
    upsertMetaByName("robots", seo.robots);
    upsertMetaByName("author", SITE_NAME);
    upsertMetaByName("geo.region", "ZA");
    upsertMetaByName("geo.placename", "South Africa");

    upsertLink("canonical", seo.canonical);

    upsertMetaByProperty("og:site_name", SITE_NAME);
    upsertMetaByProperty("og:title", seo.title);
    upsertMetaByProperty("og:description", seo.description);
    upsertMetaByProperty("og:url", seo.canonical);
    upsertMetaByProperty("og:type", seo.ogType);
    upsertMetaByProperty("og:image", image);
    upsertMetaByProperty("og:image:alt", `${SITE_NAME} – South African money toolkit`);
    upsertMetaByProperty("og:locale", "en_ZA");

    upsertMetaByName("twitter:card", "summary_large_image");
    upsertMetaByName("twitter:title", seo.title);
    upsertMetaByName("twitter:description", seo.description);
    upsertMetaByName("twitter:image", image);
    if (TWITTER_HANDLE.startsWith("@")) {
      upsertMetaByName("twitter:site", TWITTER_HANDLE);
    }

    const schemas = getJsonLdForPath(pathname);
    const existing = document.getElementById(JSON_LD_ID);
    existing?.remove();

    if (schemas.length > 0) {
      const script = document.createElement("script");
      script.id = JSON_LD_ID;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(
        schemas.length === 1 ? schemas[0] : schemas,
      );
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
};

export default SeoHead;
